import type { FC } from 'react';
import { useEffect, useState } from 'react';
import {
  Check,
  Code,
  Eye,
  FileText,
  ImageIcon,
  Mail,
  ShieldCheck,
  User,
} from 'lucide-react';
import type { InboxMessageDetail } from '@antigravity/types';

interface MessageDetailProps {
  message: InboxMessageDetail | null;
  isLoading: boolean;
  error?: string | null;
  onMarkAsRead?: (id: string) => void;
  isMarkingRead?: boolean;
}

export const MessageDetail: FC<MessageDetailProps> = ({
  message,
  isLoading,
  error,
  onMarkAsRead,
  isMarkingRead = false,
}) => {
  const [viewMode, setViewMode] = useState<'formatted' | 'plain'>('formatted');
  const [loadRemoteImages, setLoadRemoteImages] = useState(false);

  // Reset remote images policy whenever selected message changes
  useEffect(() => {
    setLoadRemoteImages(false);
  }, [message?.id]);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col p-8 space-y-6 animate-pulse">
        <div className="h-7 bg-slate-800 rounded-lg w-3/4" />
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-slate-800" />
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-slate-800 rounded w-1/3" />
            <div className="h-3 bg-slate-800 rounded w-1/4" />
          </div>
        </div>
        <div className="space-y-3 pt-6 border-t border-slate-800">
          <div className="h-4 bg-slate-800 rounded w-full" />
          <div className="h-4 bg-slate-800 rounded w-5/6" />
          <div className="h-4 bg-slate-800 rounded w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 mb-3">
          <Mail className="w-6 h-6" />
        </div>
        <h3 className="text-base font-semibold text-white">Failed to Load Message</h3>
        <p className="text-xs text-slate-400 mt-1 max-w-sm">{error}</p>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center text-slate-400">
        <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 mb-4 shadow-inner">
          <Mail className="w-7 h-7" />
        </div>
        <h3 className="text-base font-medium text-slate-300">No message selected</h3>
        <p className="text-xs text-slate-400 mt-1 max-w-xs">
          Select a conversation from the inbox list to read its contents and view details.
        </p>
      </div>
    );
  }

  // Safe HTML escaper for plain-text fallback interpolation
  const escapeHtml = (str: string) =>
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const hasRemoteImages = Boolean(
    message.html && /<img\b[^>]*src=["']https?:\/\//i.test(message.html),
  );

  // Content Security Policy preventing tracking scripts, font loading, or external frames
  const cspHeader = `default-src 'none'; img-src ${
    loadRemoteImages ? 'https: http: data: cid:' : 'data: cid:'
  }; style-src 'unsafe-inline'; font-src 'none'; frame-src 'none';`;

  // Structural neutralization: rewrite remote img src to safe data URI placeholder unless consented
  const processHtmlBody = (html: string, allowRemoteImages: boolean) => {
    if (allowRemoteImages) return html;
    return html.replace(
      /<img\b([^>]*?)\bsrc=(["'])(https?:\/\/[^"']+)\2([^>]*?)>/gi,
      '<img$1src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'24\'%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'%231e293b\' rx=\'4\'/%3E%3Ctext x=\'50%25\' y=\'50%25\' dominant-baseline=\'middle\' text-anchor=\'middle\' fill=\'%2364748b\' font-size=\'10\' font-family=\'sans-serif\'%3E[Image Blocked]%3C/text%3E%3C/svg%3E" data-blocked-src="$3"$4>',
    );
  };

  const bodyContent = message.html
    ? processHtmlBody(message.html, loadRemoteImages)
    : message.text
      ? escapeHtml(message.text).replace(/\n/g, '<br/>')
      : '<em>(Empty message body)</em>';

  const sandboxedSrcDoc = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta http-equiv="Content-Security-Policy" content="${cspHeader}">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #e2e8f0;
            background-color: transparent;
            margin: 0;
            padding: 16px;
            word-break: break-word;
          }
          a { color: #60a5fa; text-decoration: underline; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #334155; padding: 6px 10px; }
          img { max-width: 100%; height: auto; border-radius: 6px; }
          blockquote { border-left: 3px solid #475569; margin: 8px 0; padding-left: 12px; color: #94a3b8; }
        </style>
      </head>
      <body>
        ${bodyContent}
      </body>
    </html>
  `;

  return (
    <div className="h-full flex flex-col bg-slate-950 overflow-hidden">
      {/* Message Header */}
      <div className="p-6 border-b border-slate-800/80 bg-slate-900/40">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-lg font-bold text-white tracking-tight leading-snug">
            {message.subject || '(No Subject)'}
          </h2>

          <div className="flex items-center gap-2 shrink-0">
            {/* View Mode Toggle */}
            <div className="flex bg-slate-800/80 rounded-lg p-0.5 border border-slate-700/60">
              <button
                type="button"
                onClick={() => setViewMode('formatted')}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  viewMode === 'formatted'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                title="View formatted HTML in sandboxed frame"
              >
                <Code className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Formatted</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('plain')}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  viewMode === 'plain'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                title="View raw plain text"
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Plain Text</span>
              </button>
            </div>

            {/* Read Status / Action */}
            {!message.isRead && onMarkAsRead && (
              <button
                type="button"
                onClick={() => onMarkAsRead(message.id)}
                disabled={isMarkingRead}
                className="flex items-center gap-1.5 px-3 py-1 text-xs rounded-lg bg-brand-600/20 text-brand-300 border border-brand-500/30 hover:bg-brand-600/30 transition-colors disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                <span>Mark Read</span>
              </button>
            )}
          </div>
        </div>

        {/* Sender & Metadata */}
        <div className="flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300">
              <User className="w-4 h-4" />
            </div>
            <div>
              <div className="font-semibold text-slate-200">
                {message.from || 'Unknown Sender'}
              </div>
              <div className="text-[11px] text-slate-400">
                To: <span className="text-slate-300">{message.to || 'Undisclosed recipients'}</span>
                {message.cc && (
                  <span className="ml-2">Cc: <span className="text-slate-300">{message.cc}</span></span>
                )}
              </div>
            </div>
          </div>

          <div className="text-right text-[11px] text-slate-400">
            <div>{message.date || 'Unknown Date'}</div>
            {message.size != null && (
              <div className="text-[10px] text-slate-400">
                {Math.round(message.size / 1024)} KB
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Security & Tracking Privacy Notice */}
      <div className="px-6 py-2 bg-slate-900/60 border-b border-slate-800/60 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          <span>Active content sandboxed. Scripts, external frames, and cookies are blocked.</span>
        </div>

        {hasRemoteImages && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400">
              {loadRemoteImages ? 'Remote images enabled' : 'Remote images blocked for privacy'}
            </span>
            <button
              type="button"
              onClick={() => setLoadRemoteImages(!loadRemoteImages)}
              className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-brand-300 border border-slate-700 font-medium transition-colors"
            >
              {loadRemoteImages ? (
                <>
                  <Eye className="w-3 h-3" />
                  <span>Hide Images</span>
                </>
              ) : (
                <>
                  <ImageIcon className="w-3 h-3" />
                  <span>Load Images</span>
                </>
              )}
            </button>
          </div>
        )}

        {message.isRead && (
          <span className="text-[10px] text-emerald-400 font-medium">Read</span>
        )}
      </div>

      {/* Message Body Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-slate-950/60">
        {viewMode === 'formatted' && (message.html || !message.text) ? (
          <iframe
            title={`Message Body for ${message.subject || 'email'}`}
            sandbox=""
            srcDoc={sandboxedSrcDoc}
            className="w-full h-full min-h-[400px] border-0 rounded-lg bg-transparent"
          />
        ) : (
          <div className="font-mono text-xs text-slate-200 whitespace-pre-wrap leading-relaxed select-text p-4 bg-slate-900/50 rounded-xl border border-slate-800/80">
            {message.text || message.html?.replace(/<[^>]+>/g, ' ') || '(No text content)'}
          </div>
        )}
      </div>
    </div>
  );
};
